import { hoverTypes } from 'layout-dnd-utils/constants/dndAreaHoverPositionConstants';
import { ROW_DRAG_HANDLE, COLUMN_DRAG_HANDLE, MODULE_DRAG_HANDLE, EMPTY_CONTAINER_DRAG_HANDLE, TOPMOST_SECTION_ROW_DRAG_HANDLE, INSERT_NEW_SECTION_HANDLE } from 'InpageEditorUI/lib/layoutSection/constants';
const calculateDnDAreaDragHandlesFromHoverPositions = hoverPositions => {
  if (hoverPositions[0].type !== hoverTypes.INNER_TRIANGLE) {
    hoverPositions = hoverPositions.slice(1);
  }
  const handlesToGather = [];
  let leafModuleId;
  let firstColumnIdWithAnyRows;
  let firstRowIdWithMoreThanOneColumn;
  let currentHoverPositionIndex = 0;
  let nextHoverPosition;
  for (const hoverPosition of hoverPositions) {
    nextHoverPosition = hoverPositions[currentHoverPositionIndex + 1];
    if (!leafModuleId && hoverPosition.isModule && hoverPosition.numChildRows === 0) {
      leafModuleId = hoverPosition.id;
      const moduleDragHandle = {
        id: hoverPosition.id,
        layoutSectionId: hoverPosition.layoutSectionId,
        type: MODULE_DRAG_HANDLE,
        depth: hoverPosition.depth
      };
      // Special case early abort of drag handle computation if the module being
      // hovered over has a chance of overlaping other nodes in the tree
      if (hoverPosition.isFixedPosition || hoverPosition.isAbsolutePosition) {
        return [moduleDragHandle];
      } else {
        handlesToGather.push(moduleDragHandle);
      }
    } else if (!firstColumnIdWithAnyRows && hoverPosition.isColumn) {
      // If we haven't found any column handles yet (starting from the deepest hover position)..
      //  - Make any empty column have a empty container toolbar (that looks like the module toolbar).
      //    And also give the empty column a column toolbar.
      //  - Give the deepest, non-root column toolbar, even if it only has a single row in it
      if (hoverPosition.numChildRows === 0) {
        handlesToGather.push({
          id: hoverPosition.id,
          layoutSectionId: hoverPosition.layoutSectionId,
          type: EMPTY_CONTAINER_DRAG_HANDLE,
          depth: hoverPosition.depth
        });
      }
      if (hoverPosition.depth !== 0) {
        firstColumnIdWithAnyRows = hoverPosition.id;
        handlesToGather.push({
          id: hoverPosition.id,
          layoutSectionId: hoverPosition.layoutSectionId,
          parentId: nextHoverPosition.id,
          type: COLUMN_DRAG_HANDLE,
          depth: hoverPosition.depth
        });
      }
    } else if (!firstRowIdWithMoreThanOneColumn && hoverPosition.isRow) {
      // If we haven't found any row handles yet (starting from the deepest hover position)..
      //  - Make any empty row have a empty container toolbar (that looks like the module toolbar)
      //  - Give a toolbar to the first row with more than one column _or_ that has any styles
      //  - So we essentially are ignoring/hiding row toolbars for rows that have a single column and no styles
      if (hoverPosition.numChildColumns === 0) {
        handlesToGather.push({
          id: hoverPosition.id,
          layoutSectionId: hoverPosition.layoutSectionId,
          type: EMPTY_CONTAINER_DRAG_HANDLE,
          depth: hoverPosition.depth
        });
      }
      if (hoverPosition.numChildColumns > 1 || hoverPosition.hasLayoutStyles) {
        firstRowIdWithMoreThanOneColumn = hoverPosition.id;
        handlesToGather.push({
          id: hoverPosition.id,
          layoutSectionId: hoverPosition.layoutSectionId,
          parentId: nextHoverPosition.id,
          type: ROW_DRAG_HANDLE,
          depth: hoverPosition.depth
        });
      }
    }
    currentHoverPositionIndex += 1;
  }
  const topmostRowId = hoverPositions.length > 1 ? hoverPositions[hoverPositions.length - 2].id : null;
  if (topmostRowId) {
    if (topmostRowId === firstRowIdWithMoreThanOneColumn) {
      handlesToGather.pop();
    }
    const topmostRowPosition = hoverPositions[hoverPositions.length - 2];
    const parentPosition = hoverPositions[hoverPositions.length - 1];
    handlesToGather.push({
      id: topmostRowId,
      parentId: parentPosition.id,
      layoutSectionId: topmostRowPosition.layoutSectionId,
      type: INSERT_NEW_SECTION_HANDLE,
      depth: hoverPositions[hoverPositions.length - 2].depth
    });
    handlesToGather.push({
      id: topmostRowId,
      parentId: parentPosition.id,
      layoutSectionId: topmostRowPosition.layoutSectionId,
      type: TOPMOST_SECTION_ROW_DRAG_HANDLE,
      depth: topmostRowPosition.depth
    });
  }
  if (hoverPositions.length === 1) {
    handlesToGather.push({
      id: hoverPositions[0].id,
      parentId: null,
      isEmptyLayoutSection: true,
      layoutSectionId: hoverPositions[0].id,
      type: INSERT_NEW_SECTION_HANDLE,
      depth: 0
    });
  }
  return handlesToGather;
};
const calculateStaticDragHandlesFromHoverPosition = hoverPosition => {
  const dragHandle = {
    id: hoverPosition.id,
    uniquePositionId: hoverPosition.uniquePositionId,
    type: MODULE_DRAG_HANDLE,
    isStaticModule: hoverPosition.isStaticModule,
    isStaticSectionModule: hoverPosition.isStaticSectionModule,
    isFlexColumnModule: hoverPosition.isFlexColumnModule,
    isPostBodyModule: hoverPosition.isPostBodyModule,
    isModuleInRichText: hoverPosition.isModuleInRichText,
    isGlobalGroup: hoverPosition.isGlobalGroup,
    isGlobalPartial: hoverPosition.isGlobalPartial,
    hasDelegatedBounds: hoverPosition.hasDelegatedBounds,
    depth: hoverPosition.depth,
    // If a module in D&D area has fixed or absolute bounds it is treated as a "static" drag handle
    // and we still need the root ID
    layoutSectionId: hoverPosition.layoutSectionId
  };
  return [dragHandle];
};
export const calculateDragHandlesFromHoverPositions = hoverPositions => {
  if (hoverPositions.length === 0 || hoverPositions[0].type === hoverTypes.OUTSIDE_LAYOUT_SECTION) {
    return null;
  }
  const firstHoverPosition = hoverPositions[0];
  if (firstHoverPosition.isStaticModule === true || firstHoverPosition.isFlexColumnModule || firstHoverPosition.isGlobalGroup || firstHoverPosition.isGlobalPartial || firstHoverPosition.hasDelegatedBounds || firstHoverPosition.isPostBodyModule || firstHoverPosition.isModuleInRichText) {
    // Leaving this cast for now, want to clean it up but these types are a bit insane
    return calculateStaticDragHandlesFromHoverPosition(firstHoverPosition);
  } else if (firstHoverPosition.layoutSectionId) {
    return calculateDnDAreaDragHandlesFromHoverPositions(
    // If the first hover position has a layoutSectionId, then the hover
    // positions must be LayoutSectionHoverPositions
    hoverPositions);
  }
  return null;
};